import React from "react";
import { AlertCircle, RotateCcw } from "lucide-react";
import { captureException } from "../../lib/sentryLazy";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AppErrorBoundaryProps {
  children: React.ReactNode;
  /** Custom fallback UI — receives error info for contextual rendering */
  fallback?: React.ReactNode;
  /** Context label for Sentry tags (e.g. "AppealHQ", "ShieldCard") */
  context?: string;
  /** Optional callback when an error is caught */
  onError?: (error: Error, info: React.ErrorInfo) => void;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Generate a short, human-readable error ID for support references. */
function generateErrorId(): string {
  return `FP-${Date.now().toString(36).toUpperCase()}`;
}

const CHUNK_ERROR_PATTERNS = [
  "failed to fetch dynamically imported module",
  "loading chunk",
  "loading css chunk",
  "chunkloaderror",
  "is not a valid javascript mime type",
  "importing a module script failed",
  "error loading dynamically imported module",
] as const;

function isChunkLoadError(error: unknown): boolean {
  if (error instanceof Error) {
    const msg = error.message.toLowerCase();
    return CHUNK_ERROR_PATTERNS.some((p) => msg.includes(p));
  }
  if (
    error &&
    typeof error === "object" &&
    "name" in error &&
    (error as { name: string }).name === "ChunkLoadError"
  ) {
    return true;
  }
  return false;
}

const CHUNK_RELOAD_KEY = "fp_chunk_reload";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Section-level error boundary — catches render errors in child components
 * and shows a recoverable fallback UI instead of crashing the entire page.
 *
 * Unlike the page-level ErrorBoundary, this component:
 * - Resets in-place (re-renders children) rather than forcing a page reload
 * - Supports custom fallback UI via the `fallback` prop
 * - Generates an error ID for support reference
 * - Tags Sentry events with a `context` label
 *
 * Usage:
 *   <AppErrorBoundary context="ShieldCard">
 *     <ShieldCard ... />
 *   </AppErrorBoundary>
 */
export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  constructor(props: AppErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null };
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorId: generateErrorId(),
    };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    const { context, onError } = this.props;
    const { errorId } = this.state;

    // Stale chunk errors → auto-reload (one attempt) instead of showing error UI
    if (isChunkLoadError(error)) {
      if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
        sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
        window.location.reload();
        return;
      }
      // Already reloaded once — fall through to show error UI but only log as info
      captureException(error, (scope) => {
        scope.setLevel("info");
        scope.setTag("error.type", "chunk_load");
        scope.setTag("chunk.reload_attempted", "true");
        scope.setTag("error_context", context ?? "app_error_boundary");
      });
      onError?.(error, info);
      return;
    }

    // Report non-chunk errors to Sentry
    try {
      captureException(error, (scope) => {
        scope.setTag("error_context", context ?? "app_error_boundary");
        scope.setExtra("componentStack", info.componentStack);
        scope.setExtra("errorId", errorId);
        if (context) scope.setExtra("section", context);
      });
    } catch {
      // Sentry not initialized (dev env) — log to console instead
      console.error(
        `[AppErrorBoundary${context ? `:${context}` : ""}] Caught render error (${errorId}):`,
        error.message,
        "\nComponent stack:",
        info.componentStack
      );
    }

    onError?.(error, info);
  }

  resetErrorBoundary = (): void => {
    this.setState({ hasError: false, error: null, errorId: null });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Custom fallback takes priority
    if (this.props.fallback) {
      return this.props.fallback;
    }

    // Default fallback UI
    return (
      <div className="bg-white rounded-xl border border-fp-slate-200 shadow-sm p-6 text-center">
        <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-3">
          <AlertCircle className="w-5 h-5 text-fp-amber-500" />
        </div>
        <h3 className="text-lg font-semibold text-fp-navy-900 mb-1">
          Something went wrong
        </h3>
        <p className="text-sm text-fp-slate-500 mb-4 max-w-sm mx-auto">
          We encountered an unexpected error loading this section. Your packet
          and payment are safe.
        </p>
        {this.state.errorId && (
          <p className="text-[11px] text-fp-slate-400 font-mono mb-4">
            Error ID: {this.state.errorId}
          </p>
        )}
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={this.resetErrorBoundary}
            className="inline-flex items-center gap-2 px-4 py-2 bg-fp-navy-900 hover:bg-fp-navy-800 text-white font-semibold rounded-lg text-sm transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Try Again
          </button>
          <a
            href="/"
            className="text-sm text-fp-slate-500 hover:text-fp-slate-700 transition-colors"
          >
            Go to Homepage
          </a>
        </div>
      </div>
    );
  }
}

// ---------------------------------------------------------------------------
// Section-level inline fallback — compact "Unable to load" for sub-sections
// ---------------------------------------------------------------------------

/**
 * Minimal fallback for wrapping individual cards/sections within AppealHQ.
 * Shows a muted message with a retry button that resets the parent boundary.
 */
export function SectionErrorFallback({
  onRetry,
  label,
}: {
  onRetry?: () => void;
  label?: string;
}) {
  return (
    <div className="bg-fp-slate-50 rounded-xl border border-fp-slate-200 p-5 text-center">
      <p className="text-sm text-fp-slate-400">
        Unable to load {label ?? "this section"}.
      </p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-fp-teal-700 hover:text-fp-teal-600 transition-colors"
        >
          <RotateCcw className="w-3 h-3" />
          Retry
        </button>
      )}
    </div>
  );
}
